import React from 'react';
import { useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { Account, IMaterialToAccount } from '@sp-types/accountTypes';
import { getNetExposure } from '@utils/exposureUtils';
import { LinearGauge } from '@UI-Elements/Charts/LinearGauge/LinearGauge';
import { INetExposure } from '@sp-types/netExposureType';
import { SkeletonList } from './StrategiesUtils';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { FormatToShortPriceTemplate } from '../../../utils/FormatValue';
import { getExposureColor, getExposureStatus } from '../../../utils/RiskUtils';
import { Material } from '@sp-types/materialType';
interface Props {
  materialId: string;
  accountMaterial?: IMaterialToAccount;
  netExposure?: INetExposure;
  materialsMap: Record<string, Material>;
  account: Account;
}
export const ExposureSummary: React.FC<Props> = ({
  materialId,
  accountMaterial,
  netExposure,
  materialsMap,
  account,
}) => {
  const { convertionMap } = useCurrencyRates();
  const currNetExposure = React.useMemo(
    () => netExposure && getNetExposure(netExposure, convertionMap, materialId, materialId === GENERAL_ID),
    [netExposure, materialId],
  );

  const exposureColor = React.useMemo(
    () =>
      accountMaterial &&
      currNetExposure &&
      getExposureColor(getExposureStatus(accountMaterial.RiskTolerance, currNetExposure)),
    [netExposure, accountMaterial, materialId],
  );

  const volObj = React.useMemo(() => {
    const percent =
      (materialId === GENERAL_ID
        ? account.riskPercents.riskBackTesting?.[0]?.value
        : materialsMap[materialId].riskBackTesting?.[0]?.value) || 0;

    const volatility =
      materialId === GENERAL_ID ? account.riskPercents.volatility : materialsMap[materialId].volatility;

    const expValue = currNetExposure && FormatToShortPriceTemplate(Math.abs((currNetExposure * (percent || 1)) / 100));

    return { volatility, percent, expValue };
  }, [materialsMap, materialId, currNetExposure, account]);

  return accountMaterial && netExposure ? (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md items-center p-3">
      <h3 className="w-full text-lg2 py-2">
        <p>
          Exposure
          <i className="px-3 fas fa-info-circle" />
        </p>
      </h3>
      <div className="w-full">Total exposure</div>
      <div className="w-full text-xl p-2" style={{ color: exposureColor || '' }}>
        {currNetExposure && FormatToShortPriceTemplate(currNetExposure)}
      </div>
      <div className="flex gap-2 w-full justify-between py-4">
        <div className="flex flex-1 flex-col gap-2 ">
          <div className="text-lg2">Volatility risk</div>
          <div className="text-gray-450 py-1">Annual volatility</div>
          <div>{volObj.volatility}</div>
        </div>

        <div className="flex flex-1 flex-col gap-2">
          <div className="text-lg2">Potential loss (5%)</div>
          <div className="flex gap-1 h-full ">
            <div className="flex flex-col flex-1 gap-2">
              <div className="text-gray-450 py-1 w-full ">$ value</div>
              <div className="">{volObj.expValue}</div>
            </div>
            <div className="flex flex-col flex-1 gap-2">
              <div className="text-gray-450 py-1 w-full ">% value</div>
              <div className="">{volObj.percent}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-start justify-start w-full ">
        <LinearGauge riskTolerance={accountMaterial.RiskTolerance} currentValue={currNetExposure || 0} />
      </div>
    </div>
  ) : (
    <SkeletonList />
  );
};
